"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useMemo, useRef, useState } from "react"
import "swiper/css"
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { useIsDesktop } from "./common/bit/MediaQueries"
import LeftArrowIos from "./icons/LeftArrowIos"
import RightArrowIos from "./icons/RightArrowIos"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"

type SlideImage = {
  url: string
  altText: null | string
}

type PortImageGalleryProps = {
  data: any
}

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.lightTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) => props.theme.colors.faqTextColorHover};
  --p-item-bg: white;
  --p-item-bg-hover: ${(props) => props.theme.colors.port.lightBackgroundColor};
  --p-bullet-color: ${(props) =>
    props.$darkMode ? props.theme.colors.port.portBulletColor : props.theme.colors.port.grey};
  --p-bullet-active-color: ${(props) => props.theme.colors.propBlue};

  padding-block: 60px;
  background-color: var(--p-bg);
  color: var(--p-text);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
  }

  position: relative;
  overflow: hidden;
`

const StyledSliderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }

  .swiper {
    padding-block: 0 !important;
  }

  .swiper-slide {
    display: flex;
    width: 100%;
    height: 217px;
    position: relative;
    overflow: hidden;
    border-radius: 10px;

    ${(props) => props.theme.breakpoints.up("md")} {
      border-radius: 20px;
      height: 458px;
    }
    ${(props) => props.theme.breakpoints.up("xl")} {
      height: 550px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`

const StyledPagination = styled.div`
  align-self: center;
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledPaginationBullet = styled.button<{ $isActive: boolean }>`
  width: 17px;
  height: 17px;
  padding: 0;
  background-color: transparent;
  border: 1px solid
    ${({ $isActive }) => ($isActive ? "var(--p-bullet-active-color)" : "transparent")};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background-color: ${({ $isActive }) =>
      $isActive ? "var(--p-bullet-active-color)" : "var(--p-bullet-color)"};
    transition: background-color 0.3s ease;
  }
`

const StyledNavItem = styled.div<{ $disabled?: boolean }>`
  --padding-space: 5px;
  --bg-image-left: url("/icons/gallery-arrow-bg-left.svg");
  --bg-image-right: url("/icons/gallery-arrow-bg-right.svg");
  position: absolute;
  inset-block-start: 50%;
  inset-inline-end: 20px;
  transform: translateY(-50%);
  z-index: 1;
  width: 42px;
  aspect-ratio: 80/48;
  background-repeat: no-repeat;
  background-size: contain;

  display: flex;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
  transition: opacity 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
    &:hover {
      color: ${(props) => props.theme.colors.blue4rentMarketing};
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    --padding-space: 12px;
    width: 80px;
    height: 48px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
`

const StyledNavPrev = styled(StyledNavItem)<{ $locale: string }>`
  inset-inline-start: -1px;
  inset-inline-end: unset;
  background-image: var(--bg-image-left);
  padding-inline-end: var(--padding-space);
  justify-content: flex-end;
`

const StyledNavNext = styled(StyledNavItem)<{ $locale: string }>`
  inset-inline-end: -1px;
  background-image: var(--bg-image-right);
  padding-inline-start: var(--padding-space);
`

const PortImageGallery = ({ data }: PortImageGalleryProps) => {
  const locale = useLocale()
  const isDesktop = useIsDesktop()
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")
  const darkMode = extractProperty(data, "darkMode") || false
  const desktopGallery = extractProperty(data, "desktopGallery", true)
  const mobileGallery = extractProperty(data, "mobileGallery", true)

  const [activeIndex, setActiveIndex] = useState(0)

  const swiperRef = useRef<SwiperClass | null>(null)
  const slides: SlideImage[] = useMemo(() => {
    const gallery = isDesktop ? desktopGallery : mobileGallery
    return gallery || []
  }, [isDesktop, desktopGallery, mobileGallery])

  const handleBulletClick = (index: number) => {
    swiperRef.current?.slideToLoop(index)
  }

  // in case data fields are empty, return null
  if (!(blackTitle || subtitle) && slides.length === 0) {
    return null
  }

  return (
    <StyledSection id="port-image-gallery" $darkMode={darkMode}>
      {(blackTitle || subtitle) && (
        <PortContainer paddingInline={20}>
          <PortTitle
            normalText={blackTitle}
            highlightedText={blueTitle}
            subtitle={subtitle}
            richSubtitle
            centered
            titleSx={{
              fontSize: {
                xs: "50px",
                md: "80px",
              },
              lineHeight: {
                xs: "120%",
              },
            }}
            subtitleSx={{
              fontSize: {
                xs: "16px",
                md: "24px",
              },
            }}
          />
        </PortContainer>
      )}
      {slides.length > 0 && (
        <PortContainer paddingInline={20}>
          <StyledSliderWrapper>
            <Swiper
              speed={2000}
              slidesPerView={1}
              onSwiper={(swiperInstance) => {
                swiperRef.current = swiperInstance
              }}
              onSlideChange={(swiperInstance) => {
                setActiveIndex(swiperInstance.realIndex)
              }}
              style={{ position: "relative" }}
            >
              {slides.map(({ altText, url }, index) => (
                <SwiperSlide key={`${url}-${index}`}>
                  <Image
                    src={getUmbracoMediaUrl(url)}
                    alt={altText || `Slide ${index + 1}`}
                    priority
                    fill
                  />
                </SwiperSlide>
              ))}
              {slides.length > 1 && (
                <>
                  <StyledNavNext
                    $locale={locale}
                    $disabled={activeIndex === slides.length - 1}
                    onClick={() => {
                      if (activeIndex < slides.length - 1) {
                        swiperRef.current?.slideNext()
                      }
                    }}
                  >
                    {locale === "he" ? <LeftArrowIos /> : <RightArrowIos />}
                  </StyledNavNext>
                  <StyledNavPrev
                    $locale={locale}
                    $disabled={activeIndex === 0}
                    onClick={() => {
                      if (activeIndex > 0) {
                        swiperRef.current?.slidePrev()
                      }
                    }}
                  >
                    {locale === "he" ? <RightArrowIos /> : <LeftArrowIos />}
                  </StyledNavPrev>
                </>
              )}
            </Swiper>
            <StyledPagination>
              {slides.map((_: any, index: number) => (
                <StyledPaginationBullet
                  key={`bullet-${index}`}
                  $isActive={index === activeIndex}
                  onClick={() => handleBulletClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </StyledPagination>
          </StyledSliderWrapper>
        </PortContainer>
      )}
    </StyledSection>
  )
}
export default PortImageGallery
